import { DestroyRef } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { CrewMember } from "../../../../models/crew-member.model";
import { id } from "../../../form/controls";
import { FlightEditLookups } from "../../../form/flight-edit-lookups";

interface CrewValues {
    id: number | null;
    crewId: number | null;
    firstName: string | null;
    lastName: string | null;
    crewRoleId: number | null;
}

/**
 * As in the app: a crew member is picked among the organization's or typed by first and last name, and needs a
 * role. A picked one writes its names, which stay locked until it is cleared.
 */
export function crewRow(member: CrewValues | null, lookups: FlightEditLookups, destroyRef: DestroyRef) {
    const row = new FormGroup({
        id: id(member?.id ?? null),
        crewId: id(member?.crewId ?? null),
        firstName: new FormControl<string | null>(member?.firstName ?? null, Validators.required),
        lastName: new FormControl<string | null>(member?.lastName ?? null, Validators.required),
        crewRoleId: new FormControl<number | null>(member?.crewRoleId ?? null, Validators.required),
    });
    const lockNames = (crewId: number | null) => {
        for (const name of [row.controls.firstName, row.controls.lastName]) {
            if (crewId === null) name.enable({ emitEvent: false });
            else name.disable({ emitEvent: false });
        }
    };
    lockNames(row.controls.crewId.value);
    row.controls.crewId.valueChanges.pipe(takeUntilDestroyed(destroyRef)).subscribe((crewId) => {
        const crew = lookups.crews.find((candidate) => candidate.id === crewId);
        if (crew) row.patchValue({ firstName: crew.firstName, lastName: crew.lastName });
        lockNames(crewId);
    });
    return row;
}

/** As in the app: the same first and last name may be on the crew only once. */
const distinctNames: ValidatorFn = (array: AbstractControl): ValidationErrors | null => {
    // The raw value: a picked crew member's names are locked, and locked controls leave the plain value.
    const names = ((array as FormArray).getRawValue() as { firstName: string | null; lastName: string | null }[])
        .filter((member) => member.firstName?.trim() && member.lastName?.trim())
        .map((member) => `${member.firstName.trim()} ${member.lastName.trim()}`.toUpperCase());
    return new Set(names).size < names.length ? { duplicate: true } : null;
};

export function buildCrewArray(crew: CrewMember[], lookups: FlightEditLookups, destroyRef: DestroyRef) {
    const rows = crew.map((member) =>
        crewRow(
            {
                id: member.id,
                crewId: member.crewId,
                firstName: member.firstName,
                lastName: member.lastName,
                crewRoleId: member.crewRoleId ?? lookups.crewRoleIdOf(member.crewRole),
            },
            lookups,
            destroyRef,
        ),
    );
    return new FormArray(rows, { validators: distinctNames });
}

export type CrewArray = ReturnType<typeof buildCrewArray>;
